import { useState, useEffect, useRef, useCallback } from "react";

export function useI18n() {
  const detect = () => {
    try {
      const saved = localStorage.getItem("hi3ris_lang");
      if (saved === "fr" || saved === "en") return saved;
    } catch (e) {}
    return "en";
  };
  const [lang, setLangState] = useState(detect);
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem("hi3ris_lang", l); } catch (e) {}
    document.documentElement.lang = l;
  };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const t = useCallback((v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (v[lang] != null) return v[lang];
    if (v.en != null) return v.en;
    return "";
  }, [lang]);
  return { lang, setLang, t };
}

const THEME_KEY = "hi3ris_theme";
const INTRO_KEY = "hi3ris_intro_seen";

function readStorage(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}
function writeStorage(key, value) {
  try { localStorage.setItem(key, value); } catch (e) {}
}
function systemPrefersDark() {
  try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch (e) { return true; }
}

export function useTheme() {
  // The inline script in public/index.html already picked the first-paint
  // theme (saved choice > OS preference, light while the intro is pending).
  const detect = () => {
    const saved = readStorage(THEME_KEY);
    if (saved === "dark" || saved === "light") return saved;
    const current = document.documentElement.dataset.theme;
    if (current === "dark" || current === "light") return current;
    return systemPrefersDark() ? "dark" : "light";
  };
  const [theme, setThemeState] = useState(detect);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // An explicit toggle is remembered; otherwise the OS preference is followed.
  const setTheme = (t) => {
    setThemeState(t);
    writeStorage(THEME_KEY, t);
  };

  // Follow OS theme changes while the visitor hasn't picked one.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch (e) { return; }
    const onChange = (e) => {
      const saved = readStorage(THEME_KEY);
      if (saved === "dark" || saved === "light") return;
      setThemeState(e.matches ? "dark" : "light");
    };
    mq.addEventListener && mq.addEventListener("change", onChange);
    return () => mq.removeEventListener && mq.removeEventListener("change", onChange);
  }, []);

  // First-visit intro, only on a dark OS: hold in light for 1.8s, then a
  // smooth 1.6s ease-in-out fade to dark. Plays once; skipped when the
  // visitor already chose a theme or prefers reduced motion.
  useEffect(() => {
    const saved = readStorage(THEME_KEY);
    if (saved === "dark" || saved === "light") return;
    if (readStorage(INTRO_KEY) === "1" || !systemPrefersDark()) return;

    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setThemeState("dark");
      writeStorage(INTRO_KEY, "1");
      return;
    }

    const html = document.documentElement;
    const HOLD_MS = 1800;
    const FADE_MS = 1600;

    const switchId = setTimeout(() => {
      const now = readStorage(THEME_KEY);
      if (now === "dark" || now === "light") return;
      html.classList.add("theme-fade");
      setThemeState("dark");
      writeStorage(INTRO_KEY, "1");
    }, HOLD_MS);

    const cleanupId = setTimeout(() => {
      html.classList.remove("theme-fade");
    }, HOLD_MS + FADE_MS + 100);

    return () => {
      clearTimeout(switchId);
      clearTimeout(cleanupId);
      html.classList.remove("theme-fade");
    };
  }, []);

  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");
  return { theme, toggle };
}

export function useReducedMotion() {
  const [rm, setRm] = useState(() => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; }
  });
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const h = () => setRm(mq.matches);
    mq.addEventListener && mq.addEventListener("change", h);
    return () => mq.removeEventListener && mq.removeEventListener("change", h);
  }, []);
  return rm;
}

export function useInView(opts) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } });
    }, { threshold: opts?.threshold ?? 0.15, rootMargin: opts?.rootMargin ?? "0px 0px -10% 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, opts?.threshold, opts?.rootMargin]);
  return [ref, seen];
}

// Returns true while ref's element is in the viewport AND the tab is visible.
// Use to gate setIntervals / RAF loops so they pause when offscreen / backgrounded.
export function useIsLive(ref) {
  const [live, setLive] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let inView = true;
    let active = typeof document !== "undefined" ? !document.hidden : true;
    const update = () => setLive(inView && active);
    let io;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => { inView = entries.some((e) => e.isIntersecting); update(); },
        { threshold: 0.05 }
      );
      io.observe(el);
    }
    const onVis = () => { active = !document.hidden; update(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (io) io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [ref]);
  return live;
}
